function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Returns the first element of `value` if it's a non-empty string array. */
function firstStringInArray(value: unknown): string | null {
  if (!Array.isArray(value)) return null;
  const first: unknown = value[0];
  return typeof first === "string" && first.length > 0 ? first : null;
}

/**
 * Best-effort extraction of a user-displayable message from a DRF error body.
 * Returns null when the shape isn't recognized; callers should fall back to a
 * generic toast.
 *
 * Handles, in order:
 *   1. ["msg", ...]                          (top-level ValidationError)
 *   2. { detail: "msg" }                     (APIException / NotFound)
 *   3. { detail: ["msg", ...] }              (detail-as-array variant)
 *   4. { non_field_errors: ["msg", ...] }    (serializer non-field error)
 *   5. { <field>: ["msg", ...], ... }        (first field's first message)
 */
function getApiErrorMessage(error: unknown): string | null {
  const topArrayMsg = firstStringInArray(error);
  if (topArrayMsg) return topArrayMsg;

  if (!isRecord(error)) return null;

  if (typeof error.detail === "string" && error.detail.length > 0) {
    return error.detail;
  }

  const detailArrayMsg = firstStringInArray(error.detail);
  if (detailArrayMsg) return detailArrayMsg;

  const nonFieldMsg = firstStringInArray(error.non_field_errors);
  if (nonFieldMsg) return nonFieldMsg;

  for (const value of Object.values(error)) {
    const fieldMsg = firstStringInArray(value);
    if (fieldMsg) return fieldMsg;
  }

  return null;
}

export { firstStringInArray, getApiErrorMessage };
